import { School as SchoolIcon, TrendingUp, BookOpen, Calendar } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import StatTile from "@/components/shared/StatTile";
import ProgressBar from "@/components/shared/ProgressBar";
import { formatDateRange, formatMoney, formatNumber, percentOf } from "@/lib/format";
import { daysUntil } from "@/lib/campaignPhase";

/**
 * How the whole school is doing, for teachers — a compact companion to
 * their class figures, lower on the page.
 */
export default function SchoolSummaryCard({ school, campaign, phase }) {
  const wrappingUp = phase === "catch_up" || phase === "wrapping_up";
  const moneyPct = percentOf(campaign.raised_cents, campaign.money_goal_cents);
  const minutePct = percentOf(campaign.minutes_logged, campaign.minute_goal);
  const daysTile = phase === "finished"
    ? null
    : wrappingUp
    ? { label: "Days to donate", value: daysUntil(campaign.grace_ends_at) }
    : phase === "reading"
    ? { label: "Days left", value: campaign.days_remaining }
    : { label: "Days to start", value: daysUntil(campaign.start_at) };

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <SchoolIcon className="h-5 w-5 text-primary" />
          {school.name}
        </CardTitle>
        <CardDescription>
          {campaign.name} · {formatDateRange(campaign.start_at, campaign.end_at)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className={`grid gap-2 sm:gap-3 ${daysTile ? "grid-cols-3" : "grid-cols-2"}`}>
          <StatTile icon={TrendingUp} label="Raised" value={formatMoney(campaign.raised_cents)} color="primary" size="sm" />
          <StatTile icon={BookOpen} label="Minutes" value={formatNumber(campaign.minutes_logged)} size="sm" />
          {daysTile && <StatTile icon={Calendar} label={daysTile.label} value={formatNumber(daysTile.value)} size="sm" />}
        </div>
        {(campaign.money_goal_cents > 0 || campaign.minute_goal > 0) && (
          <div className="space-y-4">
            {campaign.money_goal_cents > 0 && (
              <ProgressBar
                label="School fundraising goal"
                detail={`${formatMoney(campaign.raised_cents)} / ${formatMoney(campaign.money_goal_cents)}`}
                pct={moneyPct}
                size="sm"
              />
            )}
            {campaign.minute_goal > 0 && (
              <ProgressBar
                label="School reading goal"
                detail={`${formatNumber(campaign.minutes_logged)} / ${formatNumber(campaign.minute_goal)} min`}
                pct={minutePct}
                barColor="bg-info"
                size="sm"
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
